/**
 * THE TWO SIDES - the party, and everyone standing against it.
 *
 * The party is the SIDE the GM named (`frame.partyTeam`, read through
 * `partySideOf`: every team on it, 2026-10-23). The opposition is everyone
 * else still in the fight: other sides and the teamless alike.
 * With no party named there is no way to tell the sides apart, so both are
 * empty - the GM names the party once.
 *
 * One definition, read by the danger step, the surprise at the start of a
 * fight, where the sides stand when one starts, and Stealth's opposition.
 *
 * Pure, zero React dependency.
 */
import { isInTheFight } from '../roster/inTheFight';
import { onSide, sideKeyIn, teamsIn } from '../roster/team';
import type { Combatant } from '../roster/types';

export interface Sides {
  party: Combatant[];
  opposition: Combatant[];
  /**
   * The scene has left the opposition not knowing the party is there
   * (Stealth's ladder at Unaware). A fight that starts now opens on their
   * surprise unless the GM says otherwise - see `openingPlan`.
   */
  caughtUnaware?: boolean;
  /** An order is running that a fight could keep (`orderRunning`): Start the fight asks Keep or Restart. */
  orderRunning?: boolean;
}

/**
 * The party's side from the frame's stored key (`partyTeam`, kept by its old name: the read is the migration, nothing
 * is rewritten). A key that is a team's tag reads as that team's side, so a party named before its team joined
 * another side still finds its allies.
 */
export function partySideOf(roster: readonly Combatant[], partyTeam: string | null): string | null {
  return sideKeyIn(teamsIn(roster), partyTeam);
}

export function sidesOf(roster: readonly Combatant[], partyTeam: string | null): Sides {
  const side = partySideOf(roster, partyTeam);
  if (!side) return { party: [], opposition: [] };
  const fighting = roster.filter(isInTheFight);
  const inParty = (c: Combatant) => onSide(c, side);
  return { party: fighting.filter(inParty), opposition: fighting.filter((c) => !inParty(c)) };
}

/**
 * Of those the scene calls unaware, who goes INTO A FIGHT unaware.
 *
 * With the party named: its opposition, and nobody else. Until then the
 * scene's own list is "everyone but whoever I act as" - right for a lone
 * infiltrator, but at a table it holds the other players' characters too,
 * so anyone a seat is playing is left out.
 *
 * Never whoever ACTS FIRST: being unaware lasts until one's own first
 * turn, and theirs is the one the fight opens on - no turn start will come
 * to take it off them.
 *
 * @param unawareIds - who the scene calls unaware (`hooks/useSceneUnaware`)
 * @param sides - the two sides; both empty until the party is named
 * @param playedIds - the combatants some seat is acting as
 * @param firstToAct - whose turn the fight opens on, when known
 */
export function unawareIntoFight(
  unawareIds: Iterable<string>,
  sides: Pick<Sides, 'party' | 'opposition'>,
  playedIds: ReadonlySet<string>,
  firstToAct: string | null = null,
): string[] {
  const named = sides.party.length + sides.opposition.length > 0;
  const against = new Set(sides.opposition.map((c) => c.id));
  return [...unawareIds]
    .filter((id) => (named ? against.has(id) : !playedIds.has(id)))
    .filter((id) => id !== firstToAct);
}
